import React, {
  ReactNode,
  useEffect,
  useEffectEvent,
  useState,
  useRef,
  useMemo,
  ChangeEvent,
  MouseEventHandler
} from 'react'
import { mergeRefs } from '~/common/reactUtils'
import { useJumpToMenuContext } from '~/design_system/modules/lists/JumpTo'
import { Close, Search as SearchIcon } from '~/icons'
import { useChildrenToStringL10n } from '~/common/childrenUtils'
import { groupBy, merge } from 'lodash'
import { $thm } from '~/common/themeSx'
import { AlertBanner } from '../alerts/Alerts'
import {
  Box,
  BoxProps,
  Button,
  forwardRef,
  RadioGroup,
  Stack,
  StackProps,
  Heading,
  useRadio,
  useRadioGroupContext,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Input,
  IconButton,
  InputLeftElement,
  InputRightElement,
  InputGroup
} from '../../chakra'
import { TOP_NAV_HEIGHT_PX } from '../../../common/scrollUtils'
import { useMsg } from '../../../common/localizationUtils'

type JumpToTarget = {
  id: string
  name: ReactNode
  category?: string
}

type SidebarMenuListProps = {
  onChange?: (value: string | number) => void
} & StackProps

/**
 * SidebarMenuList
 *
 * The sidebar list wrapper for navigational sidebars such as jump-to menus or filters.
 *
 * This component accepts StackProps as input params.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-sidebar-menu-list--docs
 */
export const SidebarMenuList = forwardRef(({ children, onChange, ...props }: SidebarMenuListProps, ref) => (
  <RadioGroup onChange={onChange}>
    <Stack w="200px" ref={ref} {...props}>
      {children}
    </Stack>
  </RadioGroup>
))

SidebarMenuList.displayName = 'SidebarInteractiveList'

type SidebarMenuListItemProps = {
  title: ReactNode
  value: string
  size?: 'sm' | 'md' | 'lg'
  isActive?: boolean
  styleProps?: BoxProps
  onItemClick?: () => void
}

/**
 * SidebarMenuListItem
 *
 * Single list item for SidebarMenuList and also used within SidebarJumpToList.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-sidebar-menu-list--docs
 */
export const SidebarMenuListItem = forwardRef((props: SidebarMenuListItemProps, ref) => {
  const msg = useMsg()
  const childrenToString = useChildrenToStringL10n()
  const group = useRadioGroupContext()
  const isChecked = group.value === props.value
  const { getRadioProps } = useRadio({ isChecked, ...props })
  const radio = getRadioProps()
  const itemRef = useRef<HTMLButtonElement>(null)

  const onItemActive = useEffectEvent((isActive?: boolean) => {
    /* isActive prop is true if the scroll position is within the boundaries of the section
	  area. When isActive is true, the list item button will be highlighted. */
    if (isActive) {
      group.onChange(props.value)
      // Scroll the menu item into view if it's not visible within its scrollable container
      itemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  })

  useEffect(() => {
    onItemActive(props.isActive)
  }, [props.isActive])

  const handleClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (props.value != null && group.onChange != null) group.onChange(props.value)
    props.onItemClick?.()
  }

  return (
    // @ts-ignore spreading radio props onto button has caused a bunch of TypeScript issues
    <Button
      href={`#${props.value}`}
      variant="list"
      size={props.size}
      // @ts-ignore
      onClick={handleClick}
      // @ts-ignore
      {...radio}
      aria-label={msg('common.ui.jumpto_section', { section_name: childrenToString(props.title) })}
      // The useRadio hook adds aria-checked=true which we don't want on a button
      aria-hidden={undefined}
      ref={mergeRefs(ref, itemRef)}
      whiteSpace="normal"
      py={2}
      height="auto"
      {...props.styleProps}>
      {props.title}
    </Button>
  )
})

SidebarMenuListItem.displayName = 'SidebarInteractiveItem'

/** Filter JumpTo targets by search query */
function useJumpToSearch(targets: JumpToTarget[]) {
  const [searchQuery, setSearchQuery] = useState('')
  const childrenToString = useChildrenToStringL10n()

  const filteredTargets = useMemo(() => {
    if (!searchQuery.trim()) return targets
    const query = searchQuery.toLowerCase().trim()
    return targets.filter(item => {
      const name = childrenToString(item.name, { delimiter: ' ' })
      return name.toLowerCase().includes(query)
    })
  }, [targets, searchQuery, childrenToString])

  return {
    searchQuery,
    setSearchQuery,
    filteredTargets,
    isSearching: searchQuery.trim().length > 0
  }
}

type SidebarJumpListProps = {
  title?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  sidebarWidth?: string
  boxProps?: BoxProps['sx']
  stackProps?: StackProps['sx']
  topOffset?: string
  isSearchable?: boolean
}
/**
 * SidebarJumpToList
 *
 * A sidebar Jump-to list is used alongside long content for jump to menus. References can be passed to keep track of
 * scroll position where it will automatically highlight the list item it passes. This component is only used
 * for jump-to menus.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-jump-to-menu--docs
 */
export const SidebarJumpToList = forwardRef(
  (
    {
      title,
      size = 'md',
      sidebarWidth = '201px',
      boxProps: boxPropsOverrides,
      stackProps: stackPropsOverrides,
      topOffset = TOP_NAV_HEIGHT_PX,
      isSearchable = false
    }: SidebarJumpListProps,
    ref
  ) => {
    const boxSx = merge(
      $thm({
        borderRight: '1px',
        borderRightColor: { $base: 'gray.300', $canvas: 'var(--cnvs-sys-color-border-strong)' }
      }),
      boxPropsOverrides
    )

    const stackSx = merge(
      $thm({
        gap: 0,
        paddingY: { $base: 6, $canvas: 0 },
        borderRight: '1px',
        borderRightColor: { $base: 'gray.300', $canvas: 'var(--cnvs-sys-color-border-strong)' }
      }),
      stackPropsOverrides
    )

    const msg = useMsg()
    const { targets, validation } = useJumpToMenuContext()
    const [activeSectionId, setActiveSectionId] = useState(targets[0]?.id)
    const { searchQuery, setSearchQuery, filteredTargets, isSearching } = useJumpToSearch(targets)

    // Group targets by category at render time (only when categories are present)
    const hasCategories = validation.isValid && filteredTargets.some(t => t.category)
    const categoryGroups = hasCategories ? groupBy(filteredTargets, 'category') : {}
    const categoryNames = Object.keys(categoryGroups)

    // Only track which categories the user has explicitly collapsed
    const [collapsedCategories, setCollapsedCategories] = useState<Set<number>>(() => new Set())
    // Reset collapsed categories when category structure changes
    // Uses React's "store previous value in state" pattern to detect changes synchronously
    // @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    // Update to useResetState when util becomes available
    const [prevCategoryCount, setPrevCategoryCount] = useState(categoryNames.length)
    if (categoryNames.length !== prevCategoryCount) {
      setPrevCategoryCount(categoryNames.length)
      setCollapsedCategories(new Set())
    }

    // Find which category contains the active section
    const activeCategoryIndex = categoryNames.findIndex(name =>
      categoryGroups[name].some(item => item.id === activeSectionId)
    )

    // Auto-expand category only when active section CHANGES to a collapsed category
    // (not when user manually collapses a category containing the current active section)
    const [prevActiveSectionId, setPrevActiveSectionId] = useState(activeSectionId)
    const activeSectionChanged = activeSectionId !== prevActiveSectionId
    if (activeSectionChanged) {
      setPrevActiveSectionId(activeSectionId)
      if (activeCategoryIndex !== -1 && collapsedCategories.has(activeCategoryIndex)) {
        setCollapsedCategories(prev => {
          const next = new Set(prev)
          next.delete(activeCategoryIndex)
          return next
        })
      }
    }

    // When searching, expand all categories to show results
    const expandedIndices = isSearching
      ? categoryNames.map((_, i) => i)
      : categoryNames.map((_, i) => i).filter(i => !collapsedCategories.has(i))

    useEffect(() => {
      const handleScroll = () => {
        // Figure out active section
        const targetVisibilities = targets.map(item => {
          const foundEl = item.el?.closest(`[data-section-header]`)
            ? item.el?.closest(`[data-section-container]`)
            : item.el
          if (!foundEl) return null // { percentageInView: 0, pixelsOnScreen: 0 }
          const rect = foundEl?.getBoundingClientRect()
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight
          const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
          if (visibleHeight <= 0) return null // { percentageInView: 0, pixelsOnScreen: 0 }
          const inViewPercentage = (visibleHeight / rect.height) * 100
          const elementTop = Math.max(rect.top, 0)
          const elementBottom = Math.min(rect.bottom, viewportHeight)
          const pixelsOnScreen = elementBottom - elementTop
          return { id: item.id, percentageInView: Math.round(inViewPercentage), pixelsOnScreen }
        })

        if (targetVisibilities.length === 0) return

        // If the last target is fully in view, make it active
        const lastTargetVisibility = targetVisibilities[targetVisibilities.length - 1]
        if (lastTargetVisibility?.percentageInView === 100) {
          setActiveSectionId(lastTargetVisibility.id)
          return
        }

        // Otherwise set the most active section to the first one that is fully on screen
        const firstFullyOnScreen = targetVisibilities.find(tv => tv?.percentageInView === 100)
        if (firstFullyOnScreen) {
          setActiveSectionId(firstFullyOnScreen.id)
          return
        }

        // Lastly, if not section is fully on screen, set the active section to the one with the most pixels on screen
        const mostPixelsOnScreen = targetVisibilities.reduce((champ, contender) => {
          if ((contender?.pixelsOnScreen ?? 0) > (champ?.pixelsOnScreen ?? 0)) return contender
          return champ
        }, targetVisibilities[0])
        if (mostPixelsOnScreen) {
          setActiveSectionId(mostPixelsOnScreen.id)
        }
      }

      handleScroll()

      window.addEventListener('scroll', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }, [targets])

    const isDevMode = process.env.NODE_ENV !== 'production'
    const searchInputRef = useRef<HTMLInputElement>(null)
    const clearSearch = () => setSearchQuery('')

    return (
      <Box data-sidebar-jumpto-list w={sidebarWidth} borderLeftRadius="inherit" sx={boxSx} ref={ref}>
        <Stack
          w="inherit"
          position="sticky"
          top={topOffset}
          backgroundColor="white"
          sx={stackSx}
          maxHeight={`calc(100vh - ${topOffset})`}
          overflow="hidden"
          display="flex"
          flexDirection="column">
          {title && (
            <Heading py={2} pl={4} textStyle="sectionHeading" flexShrink={0}>
              {title}
            </Heading>
          )}
          {isSearchable && (
            <Box px={4} pb={2} flexShrink={0}>
              <InputGroup size="sm">
                <InputLeftElement>
                  <SearchIcon />
                </InputLeftElement>
                <Input
                  name="jump-to-search"
                  ref={searchInputRef}
                  placeholder={msg('common.ui.search')}
                  aria-label={msg('common.ui.search_jump_links')}
                  value={searchQuery}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                />
                {searchQuery !== '' && (
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      aria-label={msg('common.ui.clear')}
                      icon={<Close />}
                      onClick={() => {
                        setSearchQuery('')
                        setTimeout(() => {
                          searchInputRef.current?.focus()
                        }, 0)
                      }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          )}
          {isDevMode && !validation.isValid && validation.errorMessage && (
            <Box px={4} pb={2} flexShrink={0}>
              <AlertBanner status="error">
                <Box>
                  {validation.errorMessage}
                  {validation.uncategorizedItems && validation.uncategorizedItems.length > 0 && (
                    <Box mt={2} fontSize="sm">
                      <Box as="strong">Missing categories:</Box>
                      <Box as="ul" mt={1} pl={4}>
                        {validation.uncategorizedItems.map(item => (
                          <Box as="li" key={item.id}>
                            {typeof item.name === 'string' ? item.name : item.id} (anchorId: {item.id})
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </AlertBanner>
            </Box>
          )}
          <Box flex={1} overflowY="auto" overflowX="hidden">
            {(() => {
              // No results message (applies to both flat and category modes)
              if (isSearching && filteredTargets.length === 0) {
                return (
                  <Box px={4} py={2} color="gray.500" fontSize="sm">
                    {msg('common.ui.no_results_found')}
                  </Box>
                )
              }

              // Render category groups with accordion
              if (hasCategories) {
                return (
                  <RadioGroup>
                    <Accordion
                      allowMultiple
                      index={expandedIndices}
                      onChange={indices => {
                        // Don't allow collapsing while searching
                        if (isSearching) return
                        const expanded = new Set(Array.isArray(indices) ? indices : [indices])
                        // Collapsed = all categories not in expanded
                        setCollapsedCategories(new Set(categoryNames.map((_, i) => i).filter(i => !expanded.has(i))))
                      }}>
                      <Stack>
                        {Object.entries(categoryGroups).map(([category, items]) => (
                          <AccordionItem key={category} border="none">
                            <AccordionButton textStyle="bodySemi">
                              <Box flex="1" textAlign="left">
                                {category}
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={0} pb={0}>
                              <Stack>
                                {items.map(item => (
                                  <SidebarMenuListItem
                                    key={item.id}
                                    title={item.name}
                                    value={item.id}
                                    size={size}
                                    isActive={item.id === activeSectionId}
                                    styleProps={{ pl: 6 }}
                                    onItemClick={clearSearch}
                                  />
                                ))}
                              </Stack>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Stack>
                    </Accordion>
                  </RadioGroup>
                )
              }

              // Render flat list (no categories or validation failed)
              return (
                <RadioGroup>
                  <Stack>
                    {filteredTargets.map(item => (
                      <SidebarMenuListItem
                        key={item.id}
                        title={item.name}
                        value={item.id}
                        size={size}
                        isActive={item.id === activeSectionId}
                        onItemClick={clearSearch}
                      />
                    ))}
                  </Stack>
                </RadioGroup>
              )
            })()}
          </Box>
        </Stack>
      </Box>
    )
  }
)

SidebarJumpToList.displayName = 'SidebarJumpToList'
